import {
  getPageRoutes,
  listPuckPages,
  loadPuckPage,
} from "@/api/cms-puck/api";
import { useSections } from "@/api/cms";
import { ItemSpotlightPickerBody } from "@/components/cms-puck/fields/ItemSpotlightPickerField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/auth/AuthContext";
import type { SupportedLocale } from "@/i18n";
import {
  normalizeSelectionCategories,
  selectionCategoryPlainTitle,
  type SelectionCategory,
  type SelectionItem,
} from "@/lib/cms-puck/blocks/selection";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { slugify } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

export function parseSlugPath(slug: string): string[] {
  if (!slug.trim()) return [];
  return slug
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function joinSlugPath(segments: string[]): string {
  return segments
    .map((s) => slugifySegment(s))
    .filter(Boolean)
    .join("/");
}

function slugifySegment(raw: string): string {
  const s = slugify(raw.trim());
  return !s || s === "untitled" ? "" : s;
}

/** Concatène des segments à un chemin texte libre (sans écraser la saisie). */
export function appendToSlugPath(current: string, extra: string[]): string {
  const cleaned = extra.map(slugifySegment).filter(Boolean);
  if (!cleaned.length) return current.trim().replace(/^\/+|\/+$/g, "");
  const base = current.trim().replace(/^\/+|\/+$/g, "");
  return base ? `${base}/${cleaned.join("/")}` : cleaned.join("/");
}

function sectionDisplayName(
  nameMultilingual: Record<string, string> | null | undefined,
  lang: string,
): string {
  if (!nameMultilingual) return "";
  return (
    nameMultilingual[lang] ||
    nameMultilingual.fr ||
    Object.values(nameMultilingual).find(Boolean) ||
    ""
  );
}

function extractSelectionCategories(layout: unknown): SelectionCategory[] {
  if (!layout || typeof layout !== "object") return [];
  const content = (layout as { content?: unknown }).content;
  if (!Array.isArray(content)) return [];
  const out: SelectionCategory[] = [];
  const walk = (nodes: unknown[]) => {
    for (const node of nodes) {
      if (!node || typeof node !== "object") continue;
      const n = node as {
        type?: string;
        props?: { categories?: unknown; [key: string]: unknown };
      };
      if (n.type === "SelectionSections" && n.props) {
        out.push(...normalizeSelectionCategories(n.props.categories));
      }
      if (n.props) {
        for (const v of Object.values(n.props)) {
          if (
            Array.isArray(v) &&
            v.every(
              (x) =>
                x != null &&
                typeof x === "object" &&
                "type" in (x as object),
            )
          ) {
            walk(v);
          }
        }
      }
    }
  };
  walk(content);
  return out;
}

type AddMode =
  | "free"
  | "page"
  | "sel-rubrique"
  | "sel-item"
  | "cr-decoupage"
  | "cr-item"
  | null;

/**
 * Champ slug éditable + menu « Ajouter » qui concatène des fragments
 * (page / sélection / CR / libre) sous forme de texte dans l'URL.
 */
export function SlugPathComposer({
  lang,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  lang: SupportedLocale;
  value: string;
  onChange: (slug: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const cmsNew = useCmsNew();
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id ?? "";
  const [addMode, setAddMode] = useState<AddMode>(null);

  const appendSegments = (extra: string[]) => {
    onChange(appendToSlugPath(value, extra));
  };

  const trimmed = value.trim().replace(/^\/+|\/+$/g, "");
  const preview = trimmed ? `/${lang}/${trimmed}` : `/${lang}/`;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <span className="whitespace-nowrap text-xs text-slate-400">
          /{lang}/
        </span>
        <Input
          id="seo-slug"
          value={disabled ? "" : value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono text-sm"
        />
        {!disabled ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="shrink-0">
                <Plus size={14} className="mr-1" />
                {cmsNew("cms.new.seo.path.add", "Ajouter")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                {cmsNew(
                  "cms.new.seo.path.addMenu",
                  "Ajouter au chemin (texte)",
                )}
              </DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => setAddMode("free")}>
                {cmsNew("cms.new.seo.path.free", "Segment libre…")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setAddMode("page")}>
                {cmsNew("cms.new.seo.path.page", "Page CMS…")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>
                {cmsNew("cms.new.seo.path.selection", "Sélection")}
              </DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => setAddMode("sel-rubrique")}>
                {cmsNew("cms.new.seo.path.selRubrique", "Rubrique…")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setAddMode("sel-item")}>
                {cmsNew(
                  "cms.new.seo.path.selItem",
                  "Œuvre d'une rubrique…",
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>
                {cmsNew(
                  "cms.new.seo.path.catalogue",
                  "Catalogue raisonné",
                )}
              </DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => setAddMode("cr-decoupage")}>
                {cmsNew("cms.new.seo.path.crDecoupage", "Découpage…")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setAddMode("cr-item")}>
                {cmsNew(
                  "cms.new.seo.path.crItem",
                  "Œuvre / multiple d'un découpage…",
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <p className="text-xs text-slate-500">
        {cmsNew("cms.new.seo.path.preview", "Aperçu")} :{" "}
        <span className="font-mono text-slate-700">{preview}</span>
      </p>

      {addMode === "free" ? (
        <FreeSegmentDialog
          open
          onOpenChange={(o) => !o && setAddMode(null)}
          onConfirm={(seg) => {
            appendSegments([seg]);
            setAddMode(null);
          }}
        />
      ) : null}
      {addMode === "page" ? (
        <PageSegmentDialog
          open
          lang={lang}
          onOpenChange={(o) => !o && setAddMode(null)}
          onConfirm={(segs) => {
            appendSegments(segs);
            setAddMode(null);
          }}
        />
      ) : null}
      {addMode === "sel-rubrique" || addMode === "sel-item" ? (
        <SelectionSegmentDialog
          open
          lang={lang}
          withItem={addMode === "sel-item"}
          onOpenChange={(o) => !o && setAddMode(null)}
          onConfirm={(segs) => {
            appendSegments(segs);
            setAddMode(null);
          }}
        />
      ) : null}
      {addMode === "cr-decoupage" || addMode === "cr-item" ? (
        <CatalogueSegmentDialog
          open
          lang={lang}
          cmsId={cmsId}
          withItem={addMode === "cr-item"}
          onOpenChange={(o) => !o && setAddMode(null)}
          onConfirm={(segs) => {
            appendSegments(segs);
            setAddMode(null);
          }}
        />
      ) : null}
    </div>
  );
}

function FreeSegmentDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: (seg: string) => void;
}) {
  const cmsNew = useCmsNew();
  const [text, setText] = useState("");
  const preview = slugifySegment(text);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {cmsNew("cms.new.seo.path.freeTitle", "Segment libre")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>{cmsNew("cms.new.seo.path.segment", "Segment")}</Label>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="custom-fiche"
            autoFocus
          />
          {preview ? (
            <p className="text-xs text-slate-500 font-mono">→ {preview}</p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {cmsNew("cms.new.auto.action.cancel", "Annuler")}
          </Button>
          <Button
            disabled={!preview}
            onClick={() => preview && onConfirm(preview)}
          >
            {cmsNew("cms.new.seo.path.append", "Ajouter")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PageSegmentDialog({
  open,
  lang,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  lang: SupportedLocale;
  onOpenChange: (o: boolean) => void;
  onConfirm: (segs: string[]) => void;
}) {
  const cmsNew = useCmsNew();
  const [pageId, setPageId] = useState("");
  const { data: pages = [] } = useQuery({
    queryKey: ["cms-puck", "pages", "slug-composer"],
    queryFn: () =>
      listPuckPages({
        excludeShellFlow: true,
        excludeMaintenancePage: true,
      }),
    enabled: open,
  });
  const { data: routes = [] } = useQuery({
    queryKey: ["cms-puck", "page-routes", pageId],
    queryFn: () => getPageRoutes(pageId),
    enabled: open && !!pageId,
  });
  const segs = useMemo(() => {
    const route =
      routes.find((r) => r.lang === lang) ?? routes[0] ?? null;
    if (!route?.slug) return [];
    return parseSlugPath(route.slug);
  }, [routes, lang]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {cmsNew("cms.new.seo.path.pageTitle", "Ajouter une page CMS")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>{cmsNew("cms.new.auto.label.page", "Page")}</Label>
          <Select value={pageId || undefined} onValueChange={setPageId}>
            <SelectTrigger className="h-9 bg-white">
              <SelectValue
                placeholder={cmsNew(
                  "cms.new.seo.path.choosePage",
                  "Choisir…",
                )}
              />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              {pages.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name || p.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {segs.length ? (
            <p className="text-xs text-slate-500 font-mono">
              → /{segs.join("/")}
            </p>
          ) : pageId ? (
            <p className="text-xs text-amber-600">
              {cmsNew(
                "cms.new.seo.path.pageNoSlug",
                "Cette page n'a pas de slug pour cette langue.",
              )}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {cmsNew("cms.new.auto.action.cancel", "Annuler")}
          </Button>
          <Button
            disabled={!segs.length}
            onClick={() => onConfirm(segs)}
          >
            {cmsNew("cms.new.seo.path.append", "Ajouter")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SelectionSegmentDialog({
  open,
  lang,
  withItem,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  lang: SupportedLocale;
  withItem: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: (segs: string[]) => void;
}) {
  const cmsNew = useCmsNew();
  const [pageId, setPageId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [item, setItem] = useState<SelectionItem | undefined>();

  const { data: pages = [] } = useQuery({
    queryKey: ["cms-puck", "pages", "slug-composer-sel"],
    queryFn: () =>
      listPuckPages({
        excludeShellFlow: true,
        excludeCatalogFlow: true,
        excludeMaintenancePage: true,
      }),
    enabled: open,
  });

  const { data: pageRecord } = useQuery({
    queryKey: ["cms-puck", "page", pageId, lang],
    queryFn: () => loadPuckPage(pageId, lang),
    enabled: open && !!pageId,
  });

  const categories = useMemo(
    () => extractSelectionCategories(pageRecord?.data),
    [pageRecord?.data],
  );
  const category = categories.find((c) => c.id === categoryId);

  const segs = useMemo(() => {
    if (!category) return [];
    const titleSlug = slugifySegment(
      selectionCategoryPlainTitle(category.title) || category.id,
    );
    const base = [titleSlug, category.id].filter(Boolean);
    if (withItem && item?.id) return [...base, item.id];
    return base;
  }, [category, withItem, item]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {withItem
              ? cmsNew(
                  "cms.new.seo.path.selItemTitle",
                  "Œuvre d'une rubrique",
                )
              : cmsNew("cms.new.seo.path.selRubriqueTitle", "Rubrique")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label>
              {cmsNew(
                "cms.new.seo.path.selectionPage",
                "Page portant la sélection",
              )}
            </Label>
            <Select
              value={pageId || undefined}
              onValueChange={(id) => {
                setPageId(id);
                setCategoryId("");
                setItem(undefined);
              }}
            >
              <SelectTrigger className="h-9 bg-white">
                <SelectValue
                  placeholder={cmsNew(
                    "cms.new.seo.path.choosePage",
                    "Choisir…",
                  )}
                />
              </SelectTrigger>
              <SelectContent className="z-[70]">
                {pages.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name || p.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {pageId ? (
            <div className="flex flex-col gap-1">
              <Label>
                {cmsNew("cms.new.seo.path.rubrique", "Rubrique")}
              </Label>
              {categories.length === 0 ? (
                <p className="text-xs text-slate-500">
                  {cmsNew(
                    "cms.new.seo.path.noRubriques",
                    "Aucune rubrique SelectionSections sur cette page.",
                  )}
                </p>
              ) : (
                <Select
                  value={categoryId || undefined}
                  onValueChange={(id) => {
                    setCategoryId(id);
                    setItem(undefined);
                  }}
                >
                  <SelectTrigger className="h-9 bg-white">
                    <SelectValue
                      placeholder={cmsNew(
                        "cms.new.seo.path.chooseRubrique",
                        "Choisir…",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {selectionCategoryPlainTitle(c.title) || c.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ) : null}
          {withItem && categoryId ? (
            <ItemSpotlightPickerBody
              selected={item}
              onPick={setItem}
              allowedTypes={["artwork", "multipleprint"]}
            />
          ) : null}
          {segs.length ? (
            <p className="text-xs text-slate-500 font-mono">
              → /{segs.join("/")}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {cmsNew("cms.new.auto.action.cancel", "Annuler")}
          </Button>
          <Button
            disabled={
              !segs.length || (withItem && !item?.id) || !categoryId
            }
            onClick={() => onConfirm(segs)}
          >
            {cmsNew("cms.new.seo.path.append", "Ajouter")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogueSegmentDialog({
  open,
  lang,
  cmsId,
  withItem,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  lang: SupportedLocale;
  cmsId: string;
  withItem: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: (segs: string[]) => void;
}) {
  const cmsNew = useCmsNew();
  const [target, setTarget] = useState<"ARTWORKS" | "MULTIPRINTS">(
    "ARTWORKS",
  );
  const [sectionId, setSectionId] = useState("");
  const [item, setItem] = useState<SelectionItem | undefined>();

  const { data: sections = [], isLoading } = useSections(
    open ? cmsId : "",
    target,
  );
  const section = sections.find((s) => s.id === sectionId);
  const sectionSlug = section
    ? slugifySegment(
        sectionDisplayName(
          section.nameMultilingual as Record<string, string>,
          lang,
        ) || section.id,
      )
    : "";

  const segs = useMemo(() => {
    if (!sectionSlug) return [];
    if (withItem && item?.id) return [sectionSlug, item.id];
    return [sectionSlug];
  }, [sectionSlug, withItem, item]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlay="none"
        stacked
        className="flex max-h-[90vh] w-[min(960px,92vw)] max-w-4xl flex-col gap-0 overflow-hidden p-0"
      >
        <div className="sticky top-0 z-10 flex shrink-0 flex-col gap-3 border-b bg-background px-6 pb-3 pt-6">
          <DialogHeader className="pr-8">
            <DialogTitle>
              {withItem
                ? cmsNew(
                    "cms.new.seo.path.crItemTitle",
                    "Œuvre / multiple d'un découpage",
                  )
                : cmsNew("cms.new.seo.path.crDecoupageTitle", "Découpage")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              {(
                [
                  ["ARTWORKS", "Œuvres"],
                  ["MULTIPRINTS", "Multiples"],
                ] as const
              ).map(([t, label]) => (
                <Button
                  key={t}
                  type="button"
                  size="sm"
                  variant={target === t ? "default" : "outline"}
                  onClick={() => {
                    setTarget(t);
                    setSectionId("");
                    setItem(undefined);
                  }}
                >
                  {cmsNew(
                    t === "ARTWORKS"
                      ? "cms.new.auto.label.uvres"
                      : "cms.new.auto.label.multiples",
                    label,
                  )}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                {cmsNew("cms.new.auto.action.cancel", "Annuler")}
              </Button>
              <Button
                size="sm"
                disabled={
                  !segs.length || (withItem && !item?.id) || !sectionId
                }
                onClick={() => onConfirm(segs)}
              >
                {cmsNew("cms.new.seo.path.append", "Ajouter")}
              </Button>
            </div>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <p className="text-xs text-slate-500">
              {cmsNew("cms.new.auto.text.chargement", "Chargement…")}
            </p>
          ) : (
            <Select
              value={sectionId || undefined}
              onValueChange={(id) => {
                setSectionId(id);
                setItem(undefined);
              }}
            >
              <SelectTrigger className="h-9 bg-white">
                <SelectValue
                  placeholder={cmsNew(
                    "cms.new.seo.path.chooseDecoupage",
                    "Choisir…",
                  )}
                />
              </SelectTrigger>
              <SelectContent className="z-[70]">
                {sections.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {sectionDisplayName(
                      s.nameMultilingual as Record<string, string>,
                      lang,
                    ) || s.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {withItem && sectionId ? (
            <ItemSpotlightPickerBody
              key={target}
              selected={item}
              onPick={setItem}
              allowedTypes={
                target === "MULTIPRINTS"
                  ? ["multipleprint"]
                  : ["artwork"]
              }
            />
          ) : null}
          {segs.length ? (
            <p className="text-xs text-slate-500 font-mono">
              → /{segs.join("/")}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
